import { createBrowserClient } from '@supabase/ssr';

import { wakeTenantAndWait } from '@/lib/api/wake-tenant';
import { createSupabaseBrowserClient } from '@/lib/supabase/client';
import {
  clearAllClientState,
  clearTenantIdFromSession,
  getTenantConfigFromSession,
  setTenantIdCookie,
  storeTenantIdInSession,
} from '@/lib/tenant/session';

import { LoginSchema } from '@/schema/auth';

import { Database } from '@/types/supabase';

/**
 * Clear all Supabase auth cookies and localStorage items
 * This is necessary when switching between tenants to prevent stale auth state
 */
function clearAllSupabaseAuth() {
  if (typeof window === 'undefined') return;

  // Get all cookies
  const cookies = document.cookie.split(';');

  // Clear all cookies that might be Supabase-related
  // Supabase SSR uses cookies like: sb-<project-ref>-auth-token, sb-<project-ref>-auth-token.code, etc.
  cookies.forEach((cookie) => {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

    // Clear any cookie that starts with 'sb-' (Supabase cookies)
    if (name.startsWith('sb-')) {
      // Clear with various path and domain combinations to ensure it's removed
      const hostname = window.location.hostname;
      const paths = ['/', '/auth', '/api'];

      paths.forEach((path) => {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${hostname}`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=.${hostname}`;
      });
    }
  });

  // Clear all localStorage items that might be Supabase auth tokens
  // These are typically stored with keys like 'sb-<project-id>-auth-token'
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('sb-')) {
      localStorage.removeItem(key);
    }
  });

  // Clear sessionStorage as well
  Object.keys(sessionStorage).forEach((key) => {
    if (key.startsWith('sb-')) {
      sessionStorage.removeItem(key);
    }
  });
}

export type EnterpriseTenant = {
  id: string;
  salon_name: string;
  name: string;
  logo_path: string | null;
  logo_text: string | null;
  supabase_url: string;
  supabase_anon_key: string;
  supabase_service_role_key: string | null;
  database_password: string | null;
};

export const fetchEnterpriseTenants = async (
  email: string,
  password: string,
): Promise<EnterpriseTenant[]> => {
  const response = await fetch('/api/auth/collect-tenant', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to fetch workspaces');
  }

  const data = await response.json();
  return data.tenants as EnterpriseTenant[];
};

export const enterpriseLogin = async ({
  email,
  password,
  tenantId,
  tenantConfig,
}: {
  email: string;
  password: string;
  tenantId: string;
  tenantConfig: {
    supabase_url: string;
    supabase_anon_key: string;
    salon_name?: string;
    supabase_service_role_key?: string | null;
    database_password?: string | null;
  };
}) => {
  // Wipe all stale session state from a previous login before establishing the new one
  clearAllClientState();

  const normalizedSupabaseUrl = tenantConfig.supabase_url.trim().endsWith('/')
    ? tenantConfig.supabase_url.trim().slice(0, -1)
    : tenantConfig.supabase_url.trim();
  const supabaseKey = tenantConfig.supabase_anon_key.trim();

  // isSingleton:false — same reason as loginUser: bypass any stale singleton.
  const supabase = createBrowserClient<Database>(
    normalizedSupabaseUrl,
    supabaseKey,
    { isSingleton: false },
  );

  const normalizedEmail = email.toLowerCase().trim();
  const authUrl = `${normalizedSupabaseUrl}/auth/v1/token?grant_type=password`;

  const directAuthResponse = await fetch(authUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: supabaseKey },
    body: JSON.stringify({ email: normalizedEmail, password }),
  });

  if (!directAuthResponse.ok) {
    const errorData = await directAuthResponse.json().catch(() => ({}));
    throw new Error(
      errorData.error_description || errorData.error || 'Authentication failed',
    );
  }

  const authData = await directAuthResponse.json();
  const session = {
    access_token: authData.access_token,
    refresh_token: authData.refresh_token,
    expires_in: authData.expires_in,
    expires_at: authData.expires_at,
    token_type: authData.token_type,
    user: authData.user,
  };

  await supabase.auth.setSession({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  });

  storeTenantIdInSession(tenantId, {
    supabase_url: tenantConfig.supabase_url,
    supabase_anon_key: tenantConfig.supabase_anon_key,
    salon_name: tenantConfig.salon_name,
    supabase_service_role_key:
      tenantConfig.supabase_service_role_key ?? undefined,
    database_password: tenantConfig.database_password ?? undefined,
    is_enterprise: true,
  });
  setTenantIdCookie(tenantId);

  // createBrowserClient handles cookie writes automatically — no manual document.cookie needed

  return { user: session.user, session };
};

export const loginUser = async (
  { email, password }: LoginSchema,
  callbacks?: { onWakingStart?: () => void; onWakingEnd?: () => void },
) => {
  console.log(email, password);
  // Wipe all stale session state from a previous login before starting new login
  clearAllClientState();

  // Step 1: Identify tenant BEFORE authentication (CRITICAL)
  // This ensures we know which tenant database to use
  let tenantConfig: {
    supabase_url: string;
    supabase_anon_key: string;
    salon_name?: string;
    is_live?: string;
  } | null = null;
  let tenantId: string | null = null;

  try {
    const identifyResponse = await fetch('/api/auth/identify-tenant', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!identifyResponse.ok) {
      const errorData = await identifyResponse.json();
      throw new Error(
        errorData.error || 'Failed to identify tenant. Please contact support.',
      );
    }

    const response = await identifyResponse.json();
    tenantId = response.tenantId;
    const config = response.tenantConfig;

    if (!tenantId) {
      throw new Error(
        'Tenant not found for this email address. Please contact support.',
      );
    }

    if (!config) {
      throw new Error(
        'Tenant configuration not returned from server. Please contact support.',
      );
    }

    if (!config.supabase_url || !config.supabase_anon_key) {
      throw new Error(
        `Tenant configuration is incomplete. URL: ${config.supabase_url || 'missing'}, Key: ${config.supabase_anon_key ? 'present' : 'missing'}`,
      );
    }

    tenantConfig = {
      supabase_url: config.supabase_url.trim(),
      supabase_anon_key: config.supabase_anon_key.trim(),
      salon_name: config.salon_name ?? undefined,
      is_live: config.is_live ?? undefined,
    };
  } catch (error) {
    // If tenant identification fails, throw error (don't proceed with auth)
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to identify tenant');
  }

  // Step 1.5: Wake idle self-hosted tenants before attempting auth against
  // their (currently stopped) Supabase project
  if (tenantConfig.is_live === 'idle' && tenantId) {
    callbacks?.onWakingStart?.();
    try {
      await wakeTenantAndWait(tenantId);
    } finally {
      callbacks?.onWakingEnd?.();
    }
  }

  // Step 2: Create tenant-specific Supabase client and authenticate
  if (!tenantConfig) {
    throw new Error('Tenant configuration not available');
  }

  // Verify tenant config values are different from default
  if (!tenantConfig.supabase_url || !tenantConfig.supabase_anon_key) {
    throw new Error('Tenant configuration is missing required fields');
  }

  // Normalize URLs and keys
  const supabaseUrl = tenantConfig.supabase_url.trim();
  const supabaseKey = tenantConfig.supabase_anon_key.trim();

  // Ensure URL is valid
  if (!supabaseUrl) {
    throw new Error('Invalid tenant URL: URL is missing');
  }

  // Ensure URL ends with proper format
  const normalizedSupabaseUrl = supabaseUrl.endsWith('/')
    ? supabaseUrl.slice(0, -1)
    : supabaseUrl;

  // Normalize email to lowercase for consistent authentication
  const normalizedEmail = email.toLowerCase().trim();

  // isSingleton:false — login must never reuse a stale/placeholder singleton.
  // After the post-login full-page reload the module re-evaluates and the real
  // tenant client becomes the singleton via createSupabaseBrowserClient().
  const supabase = createBrowserClient<Database>(
    normalizedSupabaseUrl,
    supabaseKey,
    { isSingleton: false },
  );

  const { data: authData, error: authError } =
    await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });
  if (authError) throw new Error(authError.message);

  const session = authData.session;
  const sessionUser = authData.user;
  if (!session) throw new Error('No session returned from login');

  // Step 3: Store tenant_id and config in session after successful authentication
  if (tenantId && tenantConfig) {
    storeTenantIdInSession(tenantId, tenantConfig);
    setTenantIdCookie(tenantId);
    // createBrowserClient handles cookie writes automatically — no manual document.cookie needed
  }

  return { user: sessionUser, session };
};

export const logoutUser = async () => {
  // Get tenant config from session before clearing
  const tenantConfig = getTenantConfigFromSession();

  // Create appropriate Supabase client based on tenant
  let supabase;
  if (
    tenantConfig &&
    tenantConfig.supabase_url &&
    tenantConfig.supabase_anon_key
  ) {
    // Use tenant-specific client if config exists
    supabase = createBrowserClient<Database>(
      tenantConfig.supabase_url,
      tenantConfig.supabase_anon_key,
    );
  } else {
    // Fallback to default client
    supabase = createSupabaseBrowserClient();
  }

  const { error } = await supabase.auth.signOut();

  // Clear tenant_id and config from session on logout
  clearTenantIdFromSession();

  // Clear all Supabase auth cookies and storage to prevent stale state
  clearAllSupabaseAuth();

  // Clear tenant_id cookie so middleware doesn't route to tenant Supabase on next request
  if (typeof document !== 'undefined') {
    document.cookie = 'tenant_id=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
  }

  if (error) throw new Error(error.message);
};

export const updateUserLocation = async (locationId: string) => {
  const supabase = createSupabaseBrowserClient();

  const { data, error } = await supabase.auth.updateUser({
    data: { location_id: locationId },
  });

  if (error) throw new Error(error.message);
  return data;
};

export const setUserAsAdmin = async (isAdmin: boolean = true) => {
  const supabase = createSupabaseBrowserClient();

  const { data, error } = await supabase.auth.updateUser({
    data: { is_admin: isAdmin },
  });

  if (error) throw new Error(error.message);
  return data;
};
